import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { acessoService } from "@/core/services/acessoService";
import {
  comunicadosService,
  type DadosComunicado,
  type FiltroListaComunicados,
} from "@/modules/comunicados/services/comunicadosService";

const QUERY_KEY_COMUNICADOS = ["comunicados"];
const detalheKey = (id: string) => ["comunicado", id];

/**
 * Qualquer mutação muda a lista, o preview da Início e o aviso de prazo (todos
 * sob a chave "comunicados"), e o detalhe do comunicado mexido. Roda também em
 * caso de erro: um conflito de versão (40001) significa que a tela está velha.
 */
function useInvalidarComunicados() {
  const queryClient = useQueryClient();
  return (id?: string) => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEY_COMUNICADOS });
    if (id) queryClient.invalidateQueries({ queryKey: detalheKey(id) });
  };
}

/** Uma página de comunicados publicados/arquivados. */
export function useComunicados(filtro: FiltroListaComunicados, habilitado = true) {
  return useQuery({
    queryKey: [...QUERY_KEY_COMUNICADOS, "lista", filtro.situacao, filtro.pagina],
    queryFn: () => comunicadosService.listar(filtro),
    enabled: habilitado,
    placeholderData: keepPreviousData,
  });
}

/** Os mais recentes publicados (preview da Início). */
export function useComunicadosRecentes(limite: number, habilitado = true) {
  return useQuery({
    queryKey: [...QUERY_KEY_COMUNICADOS, "recentes", limite],
    queryFn: () => comunicadosService.listarRecentes(limite),
    enabled: habilitado,
  });
}

export function useComunicado(id: string | undefined) {
  return useQuery({
    queryKey: detalheKey(id ?? ""),
    queryFn: () => comunicadosService.detalhe(id as string),
    enabled: !!id,
  });
}

/** Prazos dos publicados com "apagar sozinho" — só a gestão precisa (aviso de amanhã). */
export function usePrazosComunicados(habilitado: boolean) {
  return useQuery({
    queryKey: [...QUERY_KEY_COMUNICADOS, "prazos"],
    queryFn: comunicadosService.listarPrazosPublicados,
    enabled: habilitado,
  });
}

/**
 * Quem gerencia o elo (líder ou gestor do ministério responsável — mesma
 * função `gerencia_modulo` do banco). Só decide o que MOSTRAR; quem decide o
 * que PODE é o banco. Falha na checagem = sem permissão (fail-closed).
 */
export function usePodeGerenciarComunicados() {
  return useQuery({
    queryKey: ["comunicados-pode-gerenciar"],
    queryFn: () => acessoService.podeGerenciarModulo("comunicados"),
  });
}

/** Ministérios de que a pessoa participa — só para o rótulo "Só: …" da gestão. */
export function useMeusMinisteriosParticipo(pessoaId: string | undefined, habilitado = true) {
  return useQuery({
    queryKey: ["comunicados-meus-ministerios", pessoaId ?? ""],
    queryFn: () => comunicadosService.meusMinisteriosParticipo(pessoaId as string),
    enabled: !!pessoaId && habilitado,
  });
}

export function usePublicarComunicado() {
  const invalidar = useInvalidarComunicados();
  return useMutation({
    mutationFn: (dados: DadosComunicado) => comunicadosService.publicar(dados),
    onSettled: () => invalidar(),
  });
}

export function useEditarComunicado() {
  const invalidar = useInvalidarComunicados();
  return useMutation({
    mutationFn: ({ id, versao, dados }: { id: string; versao: number; dados: DadosComunicado }) =>
      comunicadosService.editar(id, versao, dados),
    onSettled: (_resultado, _erro, { id }) => invalidar(id),
  });
}

export function useArquivarComunicado() {
  const invalidar = useInvalidarComunicados();
  return useMutation({
    mutationFn: ({ id, versao }: { id: string; versao: number }) => comunicadosService.arquivar(id, versao),
    onSettled: (_resultado, _erro, { id }) => invalidar(id),
  });
}

export function useRestaurarComunicado() {
  const invalidar = useInvalidarComunicados();
  return useMutation({
    mutationFn: ({ id, versao }: { id: string; versao: number }) => comunicadosService.restaurar(id, versao),
    onSettled: (_resultado, _erro, { id }) => invalidar(id),
  });
}

export function useApagarComunicado() {
  const invalidar = useInvalidarComunicados();
  return useMutation({
    mutationFn: ({ id, versao }: { id: string; versao: number }) => comunicadosService.apagar(id, versao),
    onSettled: (_resultado, _erro, { id }) => invalidar(id),
  });
}
